import type { QueueItem, QueueStorage } from './queue';

/**
 * Minimální IndexedDB úložiště fronty. Bez závislosti – `idb-keyval` by tu
 * přinesl key-value model, ale fronta potřebuje číst celý obsah a mazat
 * po jednom, což je nad IDB stejně krátké.
 *
 * localStorage by nestačil: je synchronní, malý a při zavření aplikace
 * uprostřed zápisu se dá poškodit.
 */
const DB_NAME = 'coolbalik-scanner';
const DB_VERSION = 1;
const STORE = 'queue';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const req = fn(transaction.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const idbStorage: QueueStorage = {
  all: () => tx<QueueItem[]>('readonly', (s) => s.getAll() as IDBRequest<QueueItem[]>),
  put: (item) => tx('readwrite', (s) => s.put(item)).then(() => undefined),
  remove: (id) => tx('readwrite', (s) => s.delete(id)).then(() => undefined),
};

/**
 * Požádá prohlížeč, ať úložiště nemaže.
 *
 * Na WebKitu se data mažou podle LRU a po delší nečinnosti; `persist()` je
 * z toho vyjímá. Skladová aplikace je typicky přidaná na plochu a používá se
 * denně, takže by k mazání dojít nemělo – ale nespoléhat se na to.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
